import { useId, useState } from 'react'

import { PasswordToggle } from '@/components/ui/PasswordToggle'
import { cn } from '@/lib/utils/cn'

/**
 * One labelled input.
 *
 * The hint is tied to the input with `aria-describedby` rather than nested inside the
 * label. Nesting reads fine visually but makes the field's accessible *name* the label plus
 * the whole hint — so a screen reader announces a paragraph where it should announce
 * "Display name", and anything matching on the name has to know about the prose.
 */
export function Field({
  label,
  type = 'text',
  value,
  onChange,
  autoComplete,
  hint,
  placeholder,
  required = true,
  disabled = false,
}: {
  label: string
  type?: string
  value: string
  onChange: (value: string) => void
  autoComplete?: string
  hint?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
}) {
  const id = useId()
  // Per field, so "current password" and "new password" reveal independently — seeing one
  // is not a reason to expose the other.
  const [revealed, setRevealed] = useState(false)
  const isPassword = type === 'password'

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-ink-dim">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={isPassword && revealed ? 'text' : type}
          value={value}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-describedby={hint ? `${id}-hint` : undefined}
          onChange={(event) => onChange(event.target.value)}
          className={cn('field h-10', isPassword && 'pr-10')}
        />
        {isPassword && !disabled ? (
          <PasswordToggle revealed={revealed} onToggle={() => setRevealed((on) => !on)} />
        ) : null}
      </div>
      {hint ? (
        <p id={`${id}-hint`} className="mt-1 block text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

/** A saved/failed line under a form. Both outcomes need saying; only one is an error. */
export function FormNote({ tone, children }: { tone: 'ok' | 'error'; children: React.ReactNode }) {
  return (
    <p
      role={tone === 'error' ? 'alert' : 'status'}
      className={
        tone === 'error'
          ? 'rounded-lg border border-fail/25 bg-fail-wash px-3 py-2 text-sm text-fail'
          : 'text-sm font-medium text-ok'
      }
    >
      {children}
    </p>
  )
}
